import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Event as EventIcon,
  Fingerprint,
  Today,
} from '@mui/icons-material';

import {
  GWG_REMINDER_MODE_MEETING,
  GWG_REQUIREMENT_FOLDER_DEFAULT,
  IGwGFolderRequirement,
  IGwGReminder,
  describeReminder,
} from '../../../Interfaces/IGwGFolder';
import {
  ILegalClientDocumentType,
  isGwGRelevantDocumentType,
} from '../../../Interfaces/ILegalClientDocumentType';
import { ILegalTransactionMeeting } from '../../../Interfaces/ILegalTransactionMeeting';
import { GwGReminderDialog } from './GwGReminderDialog';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Alle Mandanten-Dokumentarten; gefiltert wird auf `isGwGRelevant`. */
  documentTypeArray: ILegalClientDocumentType[];
  /** Vorbelegung beim Nachkonfigurieren eines bestehenden Ordners. */
  initialRequirementArray?: IGwGFolderRequirement[];
  meetingArray: ILegalTransactionMeeting[];
  isCalendarEnabled: boolean;
  /** Anzahl beteiligter juristischer Personen — nur für den Hinweistext. */
  legalPersonCount: number;
  onSave: (requirementArray: IGwGFolderRequirement[]) => void;
}

interface IConfigRow {
  idLegalClientDocumentType: number;
  LegalClientDocumentType: string;
  /** Wird die Art in diesem Ordner überhaupt geführt? */
  isTracked: boolean;
  isMandatory: boolean;
  reminderArray: IGwGReminder[];
  /** Bestehende Anforderung, damit ihre ID beim Speichern erhalten bleibt. */
  idGwGFolderRequirement: number;
}

/** Eine Zeile der Erinnerungstabelle — Erinnerung samt ihrer Dokumentart. */
interface IReminderRow {
  row: IConfigRow;
  reminder: IGwGReminder;
}

/**
 * Konfiguration eines GwG-Ordners: welche Dokumentarten geführt werden, welche
 * davon Pflicht sind und wann erinnert wird.
 *
 * Öffnet sich beim Anlegen des Ordners und lässt sich später erneut aufrufen. Was
 * hier gesetzt wird, ist der **Standard** des Ordners — er gilt für alle
 * beteiligten juristischen Personen. Abweichungen für einzelne Beteiligte werden
 * direkt in der GwG-Ansicht gesetzt, nicht hier.
 */
export const GwGFolderConfigDialog: React.FC<IProps> = props => {
  const [rowArray, setRowArray] = useState<IConfigRow[]>([]);
  const [reminderRow, setReminderRow] = useState<IConfigRow | null>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);

  const gwgTypeArray = useMemo(
    () => props.documentTypeArray.filter(isGwGRelevantDocumentType),
    [props.documentTypeArray]
  );

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const initialArray = props.initialRequirementArray ?? [];

    setRowArray(
      gwgTypeArray.map(documentType => {
        const existing = initialArray.find(
          requirement =>
            requirement.idLegalClientDocumentType ===
              documentType.idLegalClientDocumentType &&
            requirement.LegalClient_idLegalClient ===
              GWG_REQUIREMENT_FOLDER_DEFAULT
        );

        return {
          idLegalClientDocumentType: documentType.idLegalClientDocumentType,
          LegalClientDocumentType: documentType.LegalClientDocumentType,
          /** Beim Anlegen sind zunächst alle Arten geführt und Pflicht. */
          isTracked: initialArray.length === 0 ? true : existing !== undefined,
          isMandatory: existing?.isMandatory ?? true,
          reminderArray: existing?.ReminderArray ?? [],
          idGwGFolderRequirement: existing?.idGwGFolderRequirement ?? 0,
        };
      })
    );
  }, [props.isOpen, props.initialRequirementArray, gwgTypeArray]);

  const changeRow = (
    idLegalClientDocumentType: number,
    change: Partial<IConfigRow>
  ) => {
    setRowArray(
      rowArray.map(row =>
        row.idLegalClientDocumentType === idLegalClientDocumentType
          ? { ...row, ...change }
          : row
      )
    );
  };

  const trackedRowArray = rowArray.filter(row => row.isTracked);
  const mandatoryCount = trackedRowArray.filter(row => row.isMandatory).length;

  /** Alle Erinnerungen über alle geführten Dokumentarten, flach für die Tabelle. */
  const reminderRowArray: IReminderRow[] = trackedRowArray.flatMap(row =>
    row.reminderArray.map(reminder => ({ row: row, reminder: reminder }))
  );

  const removeReminder = (target: IReminderRow) => {
    changeRow(target.row.idLegalClientDocumentType, {
      reminderArray: target.row.reminderArray.filter(
        reminder => reminder.idGwGReminder !== target.reminder.idGwGReminder
      ),
    });
  };

  const handleSave = () => {
    /**
     * Nur geführte Arten bekommen eine Standardzeile. Ohne sie führt der Ordner
     * die Dokumentart nicht — das Backend blendet sie dann in der Ansicht aus.
     * Temporäre IDs sind negativ und werden beim Speichern des Vorgangs ersetzt.
     */
    let tempId = -1;

    props.onSave(
      trackedRowArray.map(row => {
        const idRequirement =
          row.idGwGFolderRequirement > 0
            ? row.idGwGFolderRequirement
            : tempId--;

        return {
          idGwGFolderRequirement: idRequirement,
          idLegalClientDocumentType: row.idLegalClientDocumentType,
          LegalClient_idLegalClient: GWG_REQUIREMENT_FOLDER_DEFAULT,
          isMandatory: row.isMandatory,
          ReminderArray: row.reminderArray,
        };
      })
    );
    props.setIsOpen(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpen}
        onClose={() => props.setIsOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Fingerprint
            fontSize='small'
            sx={{ mr: 1, verticalAlign: 'text-bottom' }}
          />
          GwG-Ordner konfigurieren
          <Typography variant='body2' color='text.secondary'>
            Legen Sie fest, welche Unterlagen der beteiligten juristischen
            Personen geprüft werden.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {gwgTypeArray.length === 0 ? (
            <Alert severity='warning'>
              Es ist keine Mandanten-Dokumentart als GwG-relevant
              gekennzeichnet. Ohne eine solche Art kann der Ordner nichts
              prüfen.
            </Alert>
          ) : (
            <>
              <Alert severity='info' sx={{ mb: 2 }}>
                Diese Einstellungen gelten als Standard für{' '}
                {props.legalPersonCount === 1
                  ? 'die beteiligte juristische Person'
                  : `alle ${props.legalPersonCount} beteiligten juristischen Personen`}
                . Abweichungen für einen einzelnen Beteiligten setzen Sie später
                direkt im GwG-Ordner.
              </Alert>

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Dokumentart</TableCell>
                    <TableCell align='center'>Führen</TableCell>
                    <TableCell sx={{ width: 190 }}>Anforderung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowArray.map(row => (
                    <TableRow
                      key={`gwg-config-${row.idLegalClientDocumentType}`}
                      hover
                    >
                      <TableCell>
                        <Typography
                          variant='body2'
                          color={
                            row.isTracked ? 'text.primary' : 'text.disabled'
                          }
                        >
                          {row.LegalClientDocumentType}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Switch
                          size='small'
                          checked={row.isTracked}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            changeRow(row.idLegalClientDocumentType, {
                              isTracked: event.target.checked,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={row.isMandatory ? 'mandatory' : 'optional'}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            changeRow(row.idLegalClientDocumentType, {
                              isMandatory: event.target.value === 'mandatory',
                            })
                          }
                          size='small'
                          select
                          fullWidth
                          disabled={!row.isTracked}
                        >
                          <MenuItem value='mandatory'>Pflicht</MenuItem>
                          <MenuItem value='optional'>Optional</MenuItem>
                        </TextField>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mt: 1 }}
              >
                {trackedRowArray.length === 0
                  ? 'Es wird keine Dokumentart geführt — der Ordner bliebe leer.'
                  : `${trackedRowArray.length} Dokumentart${
                      trackedRowArray.length === 1 ? '' : 'en'
                    } geführt, davon ${mandatoryCount} als Pflicht. Optionale Unterlagen beeinflussen den Vollständigkeitsstatus nicht.`}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Erinnerungen
              </Typography>

              {reminderRowArray.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Es ist keine Erinnerung hinterlegt.
                </Typography>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Dokumentart</TableCell>
                      <TableCell sx={{ width: 90 }}>Art</TableCell>
                      <TableCell>Erinnerung</TableCell>
                      <TableCell align='right' sx={{ width: 100 }}>
                        Aktionen
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reminderRowArray.map(entry => (
                      <TableRow
                        key={`gwg-reminder-${entry.row.idLegalClientDocumentType}-${entry.reminder.idGwGReminder}`}
                        hover
                      >
                        <TableCell>
                          {entry.row.LegalClientDocumentType}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              entry.reminder.ReminderMode ===
                              GWG_REMINDER_MODE_MEETING
                                ? 'An einen Termin gekoppelt — verschiebt sich der Termin, wandert die Erinnerung mit.'
                                : 'Festes Datum'
                            }
                          >
                            {entry.reminder.ReminderMode ===
                            GWG_REMINDER_MODE_MEETING ? (
                              <EventIcon fontSize='small' color='action' />
                            ) : (
                              <Today fontSize='small' color='action' />
                            )}
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {describeReminder(entry.reminder)}
                          </Typography>
                          {entry.reminder.SentFor !== null &&
                            entry.reminder.SentFor !== undefined &&
                            entry.reminder.SentFor !== '' && (
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                bereits erinnert
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell align='right'>
                          <Tooltip title='Bearbeiten'>
                            <IconButton
                              size='small'
                              onClick={() => setReminderRow(entry.row)}
                            >
                              <Edit fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Entfernen'>
                            <IconButton
                              size='small'
                              onClick={() => removeReminder(entry)}
                            >
                              <Delete fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Button
                variant='outlined'
                size='small'
                startIcon={<Add />}
                disabled={trackedRowArray.length === 0}
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  setAddMenuAnchor(event.currentTarget)
                }
                sx={{ mt: 2 }}
              >
                Erinnerung hinzufügen
              </Button>

              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mt: 1 }}
              >
                Mehrere Erinnerungen am selben Tag werden zu einer E-Mail
                zusammengefasst. Empfänger ist die Systemgruppe GwG — Notar und
                zuständiger Mitarbeiter erhalten diese Erinnerungen nicht.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => props.setIsOpen(false)}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            disabled={gwgTypeArray.length === 0 || trackedRowArray.length === 0}
            onClick={handleSave}
          >
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zu welcher Dokumentart soll die neue Erinnerung gehoeren? */}
      <Menu
        open={addMenuAnchor !== null}
        anchorEl={addMenuAnchor}
        onClose={() => setAddMenuAnchor(null)}
      >
        <MenuList dense>
          {trackedRowArray.map(row => (
            <MenuItem
              key={`gwg-add-reminder-${row.idLegalClientDocumentType}`}
              onClick={() => {
                setReminderRow(row);
                setAddMenuAnchor(null);
              }}
            >
              {row.LegalClientDocumentType}
              {row.reminderArray.length > 0 && ` (${row.reminderArray.length})`}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <GwGReminderDialog
        isOpen={reminderRow !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setReminderRow(null);
          }
        }}
        documentTypeName={reminderRow?.LegalClientDocumentType ?? ''}
        participantName={null}
        reminderArray={reminderRow?.reminderArray ?? []}
        meetingArray={props.meetingArray}
        isCalendarEnabled={props.isCalendarEnabled}
        isReadOnly={false}
        onSave={reminderArray => {
          if (reminderRow !== null) {
            changeRow(reminderRow.idLegalClientDocumentType, {
              reminderArray: reminderArray,
            });
          }

          setReminderRow(null);
        }}
      />
    </>
  );
};
